import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { channelPartnerAuthAPI } from '../../services/api';
import { Card, Badge, Spinner, EmptyState, PageHeader } from '../../components/UI';
import { HiOutlineOfficeBuilding } from 'react-icons/hi';

const formatDate = (date) => {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const STATUS_LABEL = {
  active: 'Active',
  trial: 'Trial',
  expired: 'Expired',
  suspended: 'Suspended',
  cancelled: 'Cancelled',
};

const STATUS_VARIANT = {
  active: 'success',
  trial: 'warning',
  expired: 'danger',
  suspended: 'danger',
  cancelled: 'danger',
};

const PartnerOrganizations = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['partner-organizations'],
    queryFn: () => channelPartnerAuthAPI.getOrganizations({ limit: 100 }),
  });

  const organizations = data?.data?.data || [];

  return (
    <div>
      <PageHeader
        title="Referred Organizations"
        description="Businesses that registered using your referral code, regardless of payment status"
      />

      <Card padding="p-0">
        {isLoading ? (
          <div className="flex items-center justify-center py-12"><Spinner /></div>
        ) : organizations.length === 0 ? (
          <EmptyState
            icon={<HiOutlineOfficeBuilding className="w-7 h-7 text-secondary-400" />}
            title="No organizations yet"
            description="Businesses that register with your referral code will show up here."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gradient-to-r from-secondary-50 to-secondary-100 border-b-2 border-secondary-200/50 text-left">
                  <th className="px-4 py-3 text-xs font-bold text-secondary-600 uppercase tracking-wider">Organization</th>
                  <th className="px-4 py-3 text-xs font-bold text-secondary-600 uppercase tracking-wider">Plan</th>
                  <th className="px-4 py-3 text-xs font-bold text-secondary-600 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-xs font-bold text-secondary-600 uppercase tracking-wider">Joined</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-secondary-100">
                {organizations.map((org) => (
                  <tr key={org._id} className="hover:bg-gradient-to-r hover:from-primary-50/30 hover:to-transparent transition-all duration-150">
                    <td className="px-4 py-3">
                      <p className="font-medium text-secondary-900">{org.name}</p>
                      {org.email && <p className="text-xs text-secondary-400">{org.email}</p>}
                    </td>
                    <td className="px-4 py-3 text-secondary-700">{org.planId?.name || '—'}</td>
                    <td className="px-4 py-3">
                      <Badge variant={STATUS_VARIANT[org.status] || 'default'}>
                        {STATUS_LABEL[org.status] || org.status || '—'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-secondary-500">{formatDate(org.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default PartnerOrganizations;
